import { useCallback, useEffect, useState } from 'react';
import useAuth from '@/hooks/useAuth';
import { AccountTypeEnum } from '@/context/types/Account';
import { FAVOURITE_ACCOUNTS_LOCAL_STORAGE } from '@/utils/browserStorageNames';

// Scoped per business/account so favorites don't leak across profiles sharing a browser - and
// prefixed by customer id so two different people who both happen to land on the same account/
// business id (any team member of the same business shares its business.id) never see each
// other's favorites either. Favorites are a personal preference per person, not a team setting.
const getStorageKey = (scopeId?: string) =>
  `${FAVOURITE_ACCOUNTS_LOCAL_STORAGE}:${scopeId ?? 'default'}`;

const readFavoriteAccountIds = (scopeId?: string): string[] => {
  try {
    const raw = localStorage.getItem(getStorageKey(scopeId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const useFavoriteAccounts = () => {
  const { business, accountId, accountType, customer } = useAuth()!;
  const accountOrBusinessId = accountType === AccountTypeEnum.BUSINESS ? business?.id : accountId;
  // Both halves are required before this counts as a real scope - a customer id without an
  // account/business id (or vice versa, e.g. mid-login before auth context is fully populated)
  // falls back to the shared 'default' key rather than a partial, ambiguous one.
  const scopeId =
    customer?.id && accountOrBusinessId ? `${customer.id}:${accountOrBusinessId}` : undefined;
  const [favoriteAccountIds, setFavoriteAccountIds] = useState<string[]>(() =>
    readFavoriteAccountIds(scopeId)
  );

  useEffect(() => {
    setFavoriteAccountIds(readFavoriteAccountIds(scopeId));
  }, [scopeId]);

  const saveFavoriteAccountIds = useCallback(
    (walletIds: string[]) => {
      try {
        localStorage.setItem(getStorageKey(scopeId), JSON.stringify(walletIds));
      } catch {
        // Ignore storage write failures (e.g. quota exceeded / storage disabled).
      }
      setFavoriteAccountIds(walletIds);
    },
    [scopeId]
  );

  const addFavoriteAccountIds = useCallback(
    (walletIds: string[]) => {
      saveFavoriteAccountIds(Array.from(new Set([...favoriteAccountIds, ...walletIds])));
    },
    [favoriteAccountIds, saveFavoriteAccountIds]
  );

  const removeFavoriteAccountId = useCallback(
    (walletId: string) => {
      saveFavoriteAccountIds(favoriteAccountIds.filter((id) => id !== walletId));
    },
    [favoriteAccountIds, saveFavoriteAccountIds]
  );

  return {
    favoriteAccountIds,
    addFavoriteAccountIds,
    removeFavoriteAccountId,
    // Replaces the whole set, unlike addFavoriteAccountIds (which only ever adds) - for a picker
    // that lets someone both add and remove favorites in one go (see AddFavoriteAccountsModal).
    setFavoriteAccountIds: saveFavoriteAccountIds
  };
};
